import React, { useCallback, useMemo } from 'react';
import ColumnSizeSettingsStyles from './styles';
import Row from '../../Common/Row';
import Typography from '../../../kingpin/atoms/Typography';
import Dropdown from '../Dropdown';
import Toggle from '../Toggle';
import Button from '../../../kingpin/atoms/Button';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import { useContext } from 'react';
import PerformanceBoardSettingsContext from '../../PerformanceBoards/PerformanceBoardSettings/contexts/PerformanceBoardSettingsContext';

const PinnedColumnItem = ({
  columnSizeStrategy,
  setColumnSizeStrategy,
  pinnedColumn,
}: {
  columnSizeStrategy: FleetOps.ColumnSizeStrategies.FitCellContents;
  setColumnSizeStrategy: React.Dispatch<
    React.SetStateAction<FleetOps.ColumnSizeStrategies.Strategy | undefined>
  >;
  pinnedColumn: { field: string; width?: number };
}) => {
  const { board } = useContext(PerformanceBoardSettingsContext);
  const { getFieldLabel } = useGetFieldLabel();
  const isWidthEnabled = pinnedColumn.width !== undefined;

  const onWidthToggled = useCallback(() => {
    setColumnSizeStrategy({
      ...columnSizeStrategy,
      pinnedColumns: (columnSizeStrategy.pinnedColumns || []).map((p) => {
        if (p.field === pinnedColumn.field) {
          return { ...p, width: isWidthEnabled ? undefined : 100 };
        }
        return p;
      }),
    });
  }, [
    columnSizeStrategy,
    isWidthEnabled,
    pinnedColumn.field,
    setColumnSizeStrategy,
  ]);

  const onWidthChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setColumnSizeStrategy({
        ...columnSizeStrategy,
        pinnedColumns: (columnSizeStrategy.pinnedColumns || []).map((p) => {
          if (p.field === pinnedColumn.field) {
            return { ...p, width: Number.parseInt(event.target.value) };
          }
          return p;
        }),
      });
    },
    [columnSizeStrategy, pinnedColumn.field, setColumnSizeStrategy],
  );

  const onRemoveClicked = useCallback(() => {
    setColumnSizeStrategy({
      ...columnSizeStrategy,
      pinnedColumns: (columnSizeStrategy.pinnedColumns || []).filter(
        (p) => p.field !== pinnedColumn.field,
      ),
    });
  }, [columnSizeStrategy, pinnedColumn.field, setColumnSizeStrategy]);

  return (
    <div
      style={{
        padding: 8,
        borderRadius: 4,
        border: '1px solid #eaeaea',
        marginBottom: 8,
      }}
    >
      <ColumnSizeSettingsStyles.Section>
        <Row spaceBetween centerAlign>
          <Typography.Body type={'Body Bold'}>
            {getFieldLabel({
              field: pinnedColumn.field,
              dataType: board.dataType,
            })}
          </Typography.Body>
          <Button
            size="Small"
            type={'Secondary'}
            icon={'cross'}
            onClick={onRemoveClicked}
          />
        </Row>
      </ColumnSizeSettingsStyles.Section>
      <ColumnSizeSettingsStyles.OptionalNumberInput
        label={'Width'}
        isEnabled={isWidthEnabled}
        onToggled={onWidthToggled}
        value={pinnedColumn.width}
        onChange={onWidthChanged}
        testIdPrefix={`${pinnedColumn.field}-pinned-width`}
      />
    </div>
  );
};

const SpecificFieldPicker = ({
  fields,
  columnSizeStrategy,
  setColumnSizeStrategy,
}: {
  fields: { key?: string; field: string; label: string }[];
  columnSizeStrategy: FleetOps.ColumnSizeStrategies.FitCellContents;
  setColumnSizeStrategy: React.Dispatch<
    React.SetStateAction<FleetOps.ColumnSizeStrategies.Strategy | undefined>
  >;
}) => {
  const options = useMemo((): DropdownOption[] => {
    if (columnSizeStrategy.fields === undefined) {
      return [];
    }

    return fields.map(({ key, field, label }) => {
      const isSelected = columnSizeStrategy.fields?.some((f) => f === field);

      return {
        key: key || field,
        label,
        isSelected,
        onSelected: () => {
          if (isSelected) {
            setColumnSizeStrategy({
              ...columnSizeStrategy,
              fields: columnSizeStrategy.fields?.filter((f) => f !== field),
            });
            return;
          }

          setColumnSizeStrategy({
            ...columnSizeStrategy,
            fields: [...(columnSizeStrategy.fields || []), field],
          });
        },
      };
    });
  }, [columnSizeStrategy, fields, setColumnSizeStrategy]);

  return (
    <Dropdown
      options={options}
      isMulti
      isSearchEnabled
      placeholder="Add Fields"
    />
  );
};

const FitCellContents = ({
  columnSizeStrategy,
  setColumnSizeStrategy,
  fields,
}: {
  columnSizeStrategy: FleetOps.ColumnSizeStrategies.FitCellContents;
  setColumnSizeStrategy: React.Dispatch<
    React.SetStateAction<FleetOps.ColumnSizeStrategies.Strategy | undefined>
  >;
  fields: { key?: string; field: string; label: string }[];
}) => {
  const isJustSpecificFields = columnSizeStrategy.fields !== undefined;
  const pinnedColumns = useMemo(
    () => columnSizeStrategy.pinnedColumns || [],
    [columnSizeStrategy.pinnedColumns],
  );

  const pinnedColumnOptions = useMemo((): DropdownOption[] => {
    return fields
      .filter(({ field }) => !pinnedColumns.some((p) => p.field === field))
      .map(({ key, field, label }) => ({
        key: key || field,
        label,
        isSelected: false,
        onSelected: () => {
          setColumnSizeStrategy({
            ...columnSizeStrategy,
            pinnedColumns: [...pinnedColumns, { field }],
          });
        },
      }));
  }, [columnSizeStrategy, fields, pinnedColumns, setColumnSizeStrategy]);

  return (
    <>
      <ColumnSizeSettingsStyles.Section>
        <Row centerAlign>
          <div style={{ marginRight: 8 }}>
            <Toggle
              value={columnSizeStrategy.skipHeader}
              onChange={() => {
                setColumnSizeStrategy({
                  ...columnSizeStrategy,
                  skipHeader: !columnSizeStrategy.skipHeader,
                });
              }}
            />
          </div>
          <div>
            <Typography.Body type="Label">Skip Headers</Typography.Body>
          </div>
        </Row>
      </ColumnSizeSettingsStyles.Section>
      <ColumnSizeSettingsStyles.Section>
        <Row centerAlign>
          <div style={{ marginRight: 8 }}>
            <Toggle
              value={columnSizeStrategy.fields !== undefined}
              onChange={() => {
                setColumnSizeStrategy({
                  ...columnSizeStrategy,
                  fields: isJustSpecificFields ? undefined : [],
                });
              }}
              testId={'size-specific-columns-toggle'}
            />
          </div>
          <div>
            <Typography.Body type="Label">
              Size Just Specific Columns
            </Typography.Body>
          </div>
        </Row>
        {columnSizeStrategy.fields !== undefined && (
          <SpecificFieldPicker
            columnSizeStrategy={columnSizeStrategy}
            setColumnSizeStrategy={setColumnSizeStrategy}
            fields={fields}
          />
        )}
      </ColumnSizeSettingsStyles.Section>
      <ColumnSizeSettingsStyles.Section>
        <div style={{ marginBottom: 8 }}>
          <Typography.Header type={'H5'}>Pinned Columns</Typography.Header>
        </div>
        {pinnedColumns.map((p) => (
          <PinnedColumnItem
            key={p.field}
            columnSizeStrategy={columnSizeStrategy}
            setColumnSizeStrategy={setColumnSizeStrategy}
            pinnedColumn={p}
          />
        ))}
        <Dropdown options={pinnedColumnOptions} placeholder="Pin a Column" />
      </ColumnSizeSettingsStyles.Section>
    </>
  );
};

export default FitCellContents;
